/**
 * Notification bell icon with unread count badge for POS header.
 * Triggers the notification panel slide-over.
 *
 * G2.9: refreshed to follow the design-system "Mono Data / Glass Depth"
 * principle — the bell gets a subtle glass highlight when there are
 * unread items, and the badge carries a soft danger-tinted glow so it
 * reads as a pulsing fintech counter rather than a flat red dot.
 */
import { Button } from '@bbpos/ui';
import { useNotificationStore } from '@/stores/use-notification-store';
import { cn } from '@/constants/design-tokens';

export function NotificationBell() {
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const toggleDropdown = useNotificationStore((s) => s.toggleDropdown);

  const hasUnread = unreadCount > 0;
  const displayCount = unreadCount > 9 ? '9+' : String(unreadCount);

  return (
    <div
      className={cn(
        'relative rounded-full transition-all duration-200 overflow-visible',
        hasUnread && 'bg-danger-50/60 dark:bg-danger-900/20 backdrop-blur-sm shadow-[0_0_12px_-2px_rgba(239,68,68,0.35)]',
      )}
    >
      <Button
        variant="ghost"
        size="sm"
        onClick={toggleDropdown}
        aria-label={`Notifications${hasUnread ? ` (${unreadCount} unread)` : ''}`}
        className="p-2"
      >
        <svg
          className={cn(
            'h-5 w-5 transition-colors duration-200',
            hasUnread
              ? 'text-danger-600 dark:text-danger-400'
              : 'text-neutral-600 dark:text-neutral-300',
          )}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 01-3.46 0" />
        </svg>
      </Button>

      {hasUnread && (
        <span
          className={cn(
            'absolute top-0 right-0 flex items-center justify-center',
            'min-w-[18px] h-[18px] px-1 rounded-full',
            'bg-gradient-to-br from-danger-500 to-rose-600',
            'text-white text-[10px] font-bold leading-none font-mono tabular-nums',
            'ring-1 ring-white/30 dark:ring-neutral-900/60',
            'shadow-lg shadow-danger-500/40',
            'animate-pulse pointer-events-none',
          )}
          aria-hidden="true"
        >
          {displayCount}
        </span>
      )}
    </div>
  );
}
